import { create } from 'zustand';
import { trackingService } from '../services/tracking';
import type {
    OperatorLocation,
    TrackingContractPod,
    TrackingEta,
    TrackingEvent,
    TrackingFlow,
    TrackingFlowReplyMutationResponse,
    TrackingFlowReplyPayload,
    TrackingFlowStepPayload,
    TrackingState,
} from '../types/tracking';
import { toErrorMessage } from './marketplaceStoreTypes';

interface TrackingContractEntry {
    state: TrackingState | null;
    events: TrackingEvent[];
    eta: TrackingEta | null;
    pod: TrackingContractPod | null;
    flow: TrackingFlow | null;
    loading: boolean;
    error: string | null;
}

interface TrackingStore {
    byContractId: Record<number, TrackingContractEntry>;
    fetchAll: (contractId: number) => Promise<void>;
    refreshState: (contractId: number) => Promise<void>;
    refreshEta: (contractId: number) => Promise<void>;
    refreshPod: (contractId: number) => Promise<void>;
    refreshFlow: (contractId: number) => Promise<void>;
    triggerFlowStep: (contractId: number, stepKey: string, payload: TrackingFlowStepPayload) => Promise<TrackingFlow>;
    replyFlowState: (
        contractId: number,
        flowStateId: number,
        payload: TrackingFlowReplyPayload,
    ) => Promise<TrackingFlowReplyMutationResponse['data']>;
    appendEvent: (contractId: number, event: TrackingEvent) => void;
    setOperatorLocation: (contractId: number, loc: OperatorLocation) => void;
    reset: (contractId: number) => void;
}

const EMPTY_TRACKING_ENTRY: TrackingContractEntry = {
    state: null,
    events: [],
    eta: null,
    pod: null,
    flow: null,
    loading: false,
    error: null,
};

const inFlightFetchAllRequests = new Map<number, Promise<void>>();
const inFlightFlowRequests = new Map<number, Promise<void>>();

function getEntry(byContractId: Record<number, TrackingContractEntry>, contractId: number): TrackingContractEntry {
    return byContractId[contractId] ?? EMPTY_TRACKING_ENTRY;
}

function shouldFetchPod(trackingState: TrackingState | null): boolean {
    if (!trackingState) {
        return false;
    }

    if (trackingState.status === 'completed') {
        return true;
    }

    return trackingState.tracking_phase === 'at_destination'
        || trackingState.tracking_phase === 'pod_pending'
        || trackingState.tracking_phase === 'pod_confirmed';
}

function shouldFetchEta(trackingState: TrackingState | null): boolean {
    if (!trackingState) {
        return false;
    }

    return trackingState.status === 'in_transit';
}

function shouldFetchFlow(trackingState: TrackingState | null): boolean {
    if (!trackingState) {
        return false;
    }

    return trackingState.status === 'in_transit' || trackingState.status === 'completed';
}

function withInFlightDedup(
    requests: Map<number, Promise<void>>,
    contractId: number,
    requestFactory: () => Promise<void>,
): Promise<void> {
    const activeRequest = requests.get(contractId);

    if (activeRequest) {
        return activeRequest;
    }

    const nextRequest = requestFactory().finally(() => {
        requests.delete(contractId);
    });

    requests.set(contractId, nextRequest);

    return nextRequest;
}

export const useTrackingStore = create<TrackingStore>((set, get) => ({
    byContractId: {},

    fetchAll: async (contractId) => withInFlightDedup(inFlightFetchAllRequests, contractId, async () => {
        set((state) => ({
            byContractId: {
                ...state.byContractId,
                [contractId]: {
                    ...getEntry(state.byContractId, contractId),
                    loading: true,
                    error: null,
                },
            },
        }));

        try {
            const trackingState = await trackingService.getTracking(contractId);
            const [events, eta, pod, flow] = await Promise.all([
                trackingService.getEvents(contractId),
                shouldFetchEta(trackingState)
                    ? trackingService.getEta(contractId)
                    : Promise.resolve(null),
                shouldFetchPod(trackingState)
                    ? trackingService.getPod(contractId)
                    : Promise.resolve(null),
                shouldFetchFlow(trackingState)
                    ? trackingService.getFlow(contractId)
                    : Promise.resolve(null),
            ]);

            set((state) => ({
                byContractId: {
                    ...state.byContractId,
                    [contractId]: {
                        ...getEntry(state.byContractId, contractId),
                        state: trackingState,
                        events,
                        eta,
                        pod,
                        flow,
                        loading: false,
                        error: null,
                    },
                },
            }));
        } catch (error) {
            set((state) => ({
                byContractId: {
                    ...state.byContractId,
                    [contractId]: {
                        ...getEntry(state.byContractId, contractId),
                        loading: false,
                        error: toErrorMessage(error, 'No se pudo cargar el tracking del contrato.'),
                    },
                },
            }));
        }
    }),

    refreshState: async (contractId) => {
        try {
            const trackingState = await trackingService.getTracking(contractId);

            set((state) => ({
                byContractId: {
                    ...state.byContractId,
                    [contractId]: {
                        ...getEntry(state.byContractId, contractId),
                        state: trackingState,
                        flow: shouldFetchFlow(trackingState)
                            ? getEntry(state.byContractId, contractId).flow
                            : null,
                        error: null,
                    },
                },
            }));
        } catch (error) {
            set((state) => ({
                byContractId: {
                    ...state.byContractId,
                    [contractId]: {
                        ...getEntry(state.byContractId, contractId),
                        error: toErrorMessage(error, 'No se pudo refrescar el estado del tracking.'),
                    },
                },
            }));
        }
    },

    refreshEta: async (contractId) => {
        const currentEntry = getEntry(get().byContractId, contractId);

        if (!shouldFetchEta(currentEntry.state)) {
            set((state) => ({
                byContractId: {
                    ...state.byContractId,
                    [contractId]: {
                        ...getEntry(state.byContractId, contractId),
                        eta: null,
                    },
                },
            }));
            return;
        }

        try {
            const eta = await trackingService.getEta(contractId);

            set((state) => ({
                byContractId: {
                    ...state.byContractId,
                    [contractId]: {
                        ...getEntry(state.byContractId, contractId),
                        eta,
                    },
                },
            }));
        } catch (error) {
            set((state) => ({
                byContractId: {
                    ...state.byContractId,
                    [contractId]: {
                        ...getEntry(state.byContractId, contractId),
                        error: toErrorMessage(error, 'No se pudo refrescar la ETA del contrato.'),
                    },
                },
            }));
        }
    },

    refreshPod: async (contractId) => {
        try {
            const pod = await trackingService.getPod(contractId);

            set((state) => ({
                byContractId: {
                    ...state.byContractId,
                    [contractId]: {
                        ...getEntry(state.byContractId, contractId),
                        pod,
                    },
                },
            }));
        } catch (error) {
            set((state) => ({
                byContractId: {
                    ...state.byContractId,
                    [contractId]: {
                        ...getEntry(state.byContractId, contractId),
                        error: toErrorMessage(error, 'No se pudo refrescar el POD del contrato.'),
                    },
                },
            }));
        }
    },

    refreshFlow: async (contractId) => withInFlightDedup(inFlightFlowRequests, contractId, async () => {
        const currentEntry = getEntry(get().byContractId, contractId);

        if (!shouldFetchFlow(currentEntry.state)) {
            set((state) => ({
                byContractId: {
                    ...state.byContractId,
                    [contractId]: {
                        ...getEntry(state.byContractId, contractId),
                        flow: null,
                    },
                },
            }));
            return;
        }

        try {
            const flow = await trackingService.getFlow(contractId);

            set((state) => ({
                byContractId: {
                    ...state.byContractId,
                    [contractId]: {
                        ...getEntry(state.byContractId, contractId),
                        flow,
                        error: null,
                    },
                },
            }));
        } catch (error) {
            set((state) => ({
                byContractId: {
                    ...state.byContractId,
                    [contractId]: {
                        ...getEntry(state.byContractId, contractId),
                        error: toErrorMessage(error, 'No se pudo refrescar el flujo operativo.'),
                    },
                },
            }));
        }
    }),

    triggerFlowStep: async (contractId, stepKey, payload) => {
        const flow = await trackingService.triggerFlowStep(contractId, stepKey, payload);

        set((state) => ({
            byContractId: {
                ...state.byContractId,
                [contractId]: {
                    ...getEntry(state.byContractId, contractId),
                    flow,
                    error: null,
                },
            },
        }));

        return flow;
    },

    replyFlowState: async (contractId, flowStateId, payload) => {
        const response = await trackingService.replyFlowState(contractId, flowStateId, payload);

        set((state) => ({
            byContractId: {
                ...state.byContractId,
                [contractId]: {
                    ...getEntry(state.byContractId, contractId),
                    flow: response.flow,
                    error: null,
                },
            },
        }));

        return response;
    },

    appendEvent: (contractId, event) => {
        set((state) => {
            const entry = getEntry(state.byContractId, contractId);
            const alreadyExists = entry.events.some((current) => current.id === event.id);

            if (alreadyExists) {
                return state;
            }

            return {
                byContractId: {
                    ...state.byContractId,
                    [contractId]: {
                        ...entry,
                        events: [...entry.events, event].sort((left, right) => {
                            return new Date(left.created_at).getTime() - new Date(right.created_at).getTime();
                        }),
                    },
                },
            };
        });
    },

    setOperatorLocation: (contractId, loc) => {
        set((state) => {
            const entry = getEntry(state.byContractId, contractId);
            const currentState = entry.state;

            if (!currentState) {
                return state;
            }

            return {
                byContractId: {
                    ...state.byContractId,
                    [contractId]: {
                        ...entry,
                        state: {
                            ...currentState,
                            operator_last_location: loc,
                            driver_last_location: loc,
                        },
                    },
                },
            };
        });
    },

    reset: (contractId) => {
        const currentEntries = get().byContractId;

        if (!(contractId in currentEntries)) {
            return;
        }

        const nextEntries = { ...currentEntries };
        delete nextEntries[contractId];

        set({ byContractId: nextEntries });
    },
}));
